import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { formatFileSize } from '../Forms/utils';

import usePrevious from '../../hooks/usePrevious';

const StyledImagesContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isRemoveOnly',
})(({ isRemoveOnly }) => ({
  ...(isRemoveOnly
    ? {
        display: 'flex',
        marginTop: '1rem',
        gap: '1rem',
      }
    : {
        marginTop: '0.5rem',
      }),
}));

const StyledImageTitle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isRemoveOnly',
})(({ isRemoveOnly }) => ({
  ...(isRemoveOnly
    ? {
        order: 1,
      }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }),
}));

const UploadPreviewComponent = (props) => {
  const { t } = useTransl();
  const {
    name,
    size,
    url,
    onRemoveItem,
    file,
    height = '15rem',
    isSquare = true,
    isMulti,
  } = props;

  const [imageUrl, setImageUrl] = useState(url);
  const prevUrl = usePrevious(url);

  useEffect(() => {
    if (!imageUrl && file) {
      const url = URL.createObjectURL(file);
      url && setImageUrl(url);
    }
    return () => imageUrl && URL.revokeObjectURL(imageUrl);
  }, []);

  useEffect(() => {
    if (prevUrl && prevUrl !== url) setImageUrl(url);
  }, [url]);

  const isRemoveOnly = onRemoveItem && !name && !size;

  return isMulti ? (
    <StyledImagesContainer isRemoveOnly={isRemoveOnly}>
      {url.map((item) => (
        <Box key={item.url}>
          <StyledImageTitle isRemoveOnly={isRemoveOnly}>
            <Box display="flex" flexDirection="column">
              {name && (
                <strong style={{ display: 'flex', flexDirection: 'column' }}>
                  {name}
                </strong>
              )}
              {size && (
                <Typography variant="subtitle2">
                  {formatFileSize(size)}
                </Typography>
              )}
            </Box>
          </StyledImageTitle>
          {imageUrl && (
            <ExpandableImageComponent
              src={item.url}
              height={height}
              caption={name}
              isSquare={isSquare}
            />
          )}
        </Box>
      ))}
      {onRemoveItem && (
        <IconButton
          onClick={() => onRemoveItem()}
          color="inherit"
          aria-label={t('ariaLabel.clear')}
        >
          {isRemoveOnly ? <DeleteIcon /> : <ClearIcon />}
        </IconButton>
      )}
    </StyledImagesContainer>
  ) : (
    <StyledImagesContainer isRemoveOnly={isRemoveOnly}>
      <StyledImageTitle isRemoveOnly={isRemoveOnly}>
        <Box display="flex" flexDirection="column">
          {name && (
            <strong style={{ display: 'flex', flexDirection: 'column' }}>
              {name}
            </strong>
          )}
          {size && (
            <Typography variant="subtitle2">{formatFileSize(size)}</Typography>
          )}
        </Box>
        {onRemoveItem && (
          <IconButton
            onClick={() => onRemoveItem()}
            color="inherit"
            aria-label={t('ariaLabel.clear')}
          >
            {isRemoveOnly ? <DeleteIcon /> : <ClearIcon />}
          </IconButton>
        )}
      </StyledImageTitle>
      {imageUrl && (
        <ExpandableImageComponent
          src={imageUrl}
          height={height}
          caption={name}
          isSquare={isSquare}
        />
      )}
    </StyledImagesContainer>
  );
};

UploadPreviewComponent.defaultProps = {
  onRemoveItem: null,
  name: null,
  size: null,
  url: null,
};

UploadPreviewComponent.propTypes = {
  onRemoveItem: PropTypes.func,
  name: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  height: PropTypes.string,
  file: PropTypes.instanceOf(Object),
  isSquare: PropTypes.bool,
  isMulti: PropTypes.bool,
};

export default UploadPreviewComponent;
